import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const CONSENT_KEY = "capevremuri_cookie_consent";

type ConsentStatus = "accepted" | "rejected" | null;

function getConsent(): ConsentStatus {
  const value = localStorage.getItem(CONSENT_KEY);
  if (value === "accepted" || value === "rejected") return value;
  return null;
}

function setConsent(status: "accepted" | "rejected") {
  localStorage.setItem(CONSENT_KEY, status);
}

const CookieConsentBanner: React.FC = () => {
  const { t } = useLanguage();
  const b = t.cookieBanner;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show banner only if user hasn't made a choice yet
    if (getConsent() === null) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    setConsent("accepted");
    setVisible(false);
  };

  const handleReject = () => {
    setConsent("rejected");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] animate-slide-up">
      <div className="bg-white border-t-2 border-romanian-blue shadow-2xl">
        <div className="container-custom py-4 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Icon & Text */}
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-romanian-gold flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700">
                <p className="font-semibold text-gray-900 mb-1">{b.title}</p>
                <p>
                  {b.text}{" "}
                  <Link
                    to="/cookie-policy"
                    className="text-romanian-blue font-medium hover:underline"
                  >
                    {b.learnMore}
                  </Link>
                </p>
              </div>
            </div>
            {/* Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <button
                onClick={handleReject}
                className="flex-1 sm:flex-initial px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {b.reject}
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 sm:flex-initial px-5 py-2 text-sm font-medium text-white bg-romanian-blue hover:bg-romanian-blue/90 rounded-lg transition-colors"
              >
                {b.accept}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Small floating button to re-open cookie preferences.
 * Shown only after the user has already made a choice.
 */
export const CookieSettingsButton: React.FC = () => {
  const { t } = useLanguage();
  const b = t.cookieBanner;
  const [consent, setConsentState] = useState<ConsentStatus>(getConsent());
  const [showPanel, setShowPanel] = useState(false);

  // Don't show if user hasn't made a choice yet (banner is showing)
  if (consent === null) return null;

  const handleRevoke = () => {
    localStorage.removeItem(CONSENT_KEY);
    setConsentState(null);
    setShowPanel(false);
    // Force re-render by reloading — the banner will appear again
    window.location.reload();
  };

  return (
    <>
      {/* Small cookie icon button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="fixed bottom-4 left-4 z-[9998] w-10 h-10 bg-white border border-gray-200 rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors"
        title={b.settingsTooltip}
        aria-label={b.settingsTooltip}
      >
        <Cookie className="w-5 h-5 text-romanian-gold" />
      </button>

      {/* Settings panel */}
      {showPanel && (
        <div className="fixed bottom-16 left-4 z-[9999] w-80 bg-white border border-gray-200 rounded-xl shadow-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900 text-sm">
              {b.settingsTitle}
            </h3>
            <button
              onClick={() => setShowPanel(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            {consent === "accepted" ? b.statusAccepted : b.statusRejected}
          </p>
          <div className="flex gap-2">
            <button
              onClick={handleRevoke}
              className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-romanian-blue hover:bg-romanian-blue/90 rounded-lg transition-colors"
            >
              {b.revoke}
            </button>
          </div>
          <Link
            to="/cookie-policy"
            className="block text-xs text-romanian-blue hover:underline mt-2 text-center"
            onClick={() => setShowPanel(false)}
          >
            {b.learnMore}
          </Link>
        </div>
      )}
    </>
  );
};

export default CookieConsentBanner;
